import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export interface CalendarProps {
  className?: string;
  classNames?: {
    months?: string;
    month?: string;
    caption?: string;
    caption_label?: string;
    nav?: string;
    nav_button?: string;
    nav_button_previous?: string;
    nav_button_next?: string;
    table?: string;
    head_row?: string;
    head_cell?: string;
    row?: string;
    cell?: string;
    day?: string;
    day_range_end?: string;
    day_selected?: string;
    day_today?: string;
    day_outside?: string;
    day_disabled?: string;
    day_range_middle?: string;
    day_hidden?: string;
  };
  showOutsideDays?: boolean;
  mode?: "single" | "multiple" | "range";
  selected?: Date | Date[] | { from: Date; to: Date };
  onSelect?: (date: Date | undefined) => void;
  initialFocus?: boolean;
}

const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      className,
      classNames,
      showOutsideDays = true,
      mode = "single",
      selected,
      onSelect,
      initialFocus = false,
      ...props
    },
    ref
  ) => {
    const [currentMonth, setCurrentMonth] = React.useState(new Date());
    const [focusedDate, setFocusedDate] = React.useState<Date | null>(null);

    const today = new Date();
    const currentYear = currentMonth.getFullYear();
    const currentMonthIndex = currentMonth.getMonth();

    // Gerar dias do mês
    const firstDayOfMonth = new Date(currentYear, currentMonthIndex, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonthIndex + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days = [];

    // Dias do mês anterior
    if (showOutsideDays) {
      const prevMonth = new Date(currentYear, currentMonthIndex - 1, 0);
      const daysInPrevMonth = prevMonth.getDate();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({
          date: new Date(
            currentYear,
            currentMonthIndex - 1,
            daysInPrevMonth - i
          ),
          isCurrentMonth: false,
          isToday: false,
        });
      }
    }

    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonthIndex, day);
      const isToday = date.toDateString() === today.toDateString();
      days.push({
        date,
        isCurrentMonth: true,
        isToday,
      });
    }

    // Dias do próximo mês
    if (showOutsideDays) {
      const remainingDays = 42 - days.length; // 6 semanas * 7 dias
      for (let day = 1; day <= remainingDays; day++) {
        days.push({
          date: new Date(currentYear, currentMonthIndex + 1, day),
          isCurrentMonth: false,
          isToday: false,
        });
      }
    }

    const isSelected = (date: Date) => {
      if (!selected) return false;
      if (mode === "single") {
        return (
          selected instanceof Date &&
          date.toDateString() === selected.toDateString()
        );
      }
      if (mode === "multiple" && Array.isArray(selected)) {
        return selected.some((d) => d.toDateString() === date.toDateString());
      }
      if (
        mode === "range" &&
        selected &&
        typeof selected === "object" &&
        "from" in selected
      ) {
        const { from, to } = selected as { from: Date; to?: Date };
        if (!from) return false;
        const end = to || from;
        const t = new Date(date).setHours(0, 0, 0, 0);
        const fromT = new Date(from).setHours(0, 0, 0, 0);
        const toT = new Date(end).setHours(0, 0, 0, 0);
        return t >= fromT && t <= toT;
      }
      return false;
    };

    const isRangeEdge = (date: Date) => {
      if (
        mode !== "range" ||
        !selected ||
        typeof selected !== "object" ||
        !("from" in selected)
      ) {
        return false;
      }
      const { from, to } = selected as { from: Date; to?: Date };
      if (!from) return false;
      const key = date.toDateString();
      return key === from.toDateString() || key === (to || from).toDateString();
    };

    const isRangeMiddle = (date: Date) => {
      if (
        mode !== "range" ||
        !selected ||
        typeof selected !== "object" ||
        !("from" in selected)
      ) {
        return false;
      }
      const { from, to } = selected as { from: Date; to?: Date };
      if (!from || !to) return false;
      const t = new Date(date).setHours(0, 0, 0, 0);
      const fromT = new Date(from).setHours(0, 0, 0, 0);
      const toT = new Date(to).setHours(0, 0, 0, 0);
      return t > fromT && t < toT;
    };

    const handleDateClick = (date: Date) => {
      if (onSelect) {
        onSelect(date);
      }
    };

    const navigateMonth = (direction: "prev" | "next") => {
      setCurrentMonth((prev) => {
        const newMonth = new Date(prev);
        if (direction === "prev") {
          newMonth.setMonth(prev.getMonth() - 1);
        } else {
          newMonth.setMonth(prev.getMonth() + 1);
        }
        return newMonth;
      });
    };

    const monthNames = [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ];

    const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

    return (
      <div ref={ref} className={cn("p-3", className)} {...props}>
        <div
          className={cn(
            "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            classNames?.months
          )}
        >
          <div className={cn("space-y-4", classNames?.month)}>
            <div
              className={cn(
                "flex justify-center pt-1 relative items-center",
                classNames?.caption
              )}
            >
              <h2
                className={cn("text-sm font-medium", classNames?.caption_label)}
              >
                {monthNames[currentMonthIndex]} {currentYear}
              </h2>
              <div
                className={cn("space-x-1 flex items-center", classNames?.nav)}
              >
                <button
                  type="button"
                  onClick={() => navigateMonth("prev")}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                    "absolute left-1",
                    classNames?.nav_button,
                    classNames?.nav_button_previous
                  )}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => navigateMonth("next")}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                    "absolute right-1",
                    classNames?.nav_button,
                    classNames?.nav_button_next
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div
              className={cn(
                "w-full border-collapse space-y-1",
                classNames?.table
              )}
            >
              <div className={cn("flex", classNames?.head_row)}>
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className={cn(
                      "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                      classNames?.head_cell
                    )}
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => (
                  <div
                    key={index}
                    className={cn(
                      "h-9 w-9 text-center text-sm p-0 relative",
                      classNames?.cell
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleDateClick(day.date)}
                      className={cn(
                        buttonVariants({ variant: "ghost" }),
                        "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
                        !day.isCurrentMonth &&
                          "text-muted-foreground opacity-50",
                        day.isToday &&
                          !isSelected(day.date) &&
                          "bg-accent text-accent-foreground",
                        mode === "range" &&
                          isRangeMiddle(day.date) &&
                          "bg-primary/15 text-primary hover:bg-primary/25 rounded-md",
                        mode === "range" &&
                          isRangeEdge(day.date) &&
                          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-full",
                        mode !== "range" &&
                          isSelected(day.date) &&
                          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                        classNames?.day
                      )}
                    >
                      {day.date.getDate()}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

Calendar.displayName = "Calendar";

export { Calendar };
